import type { StateCreator } from 'zustand'
import { apiService } from '@/service/api'
import type {
  UpdateWorkspaceRequest,
  Workspace,
  WorkspaceOperation,
  DocumentPosition,
} from '@/service/api'
import type { loadingState } from './loadingSlice'
import type { authSessionState } from './authSessionSlice'
import { isAnnotatorRole } from '@/types/users'
import type { ChatState } from './chatSlice'
import { toast } from 'sonner'
import type { DocumentRecord } from '@/types/catalog'
import { ICON_WIDTH, ICON_HEIGHT } from '@/routes/IntelligentBoard/DocumentPreviewShape'
import { GRID_LAYOUT, DOCS_PER_PAGE, resolveBlobDocId, sortByFilename } from '@/routes/IntelligentBoard/constants'
import { primeDocumentCache, clearDocumentCache } from '@/routes/IntelligentBoard/DocumentPreviewShape/DocumentPreview'
import { preloadDocumentBlobs, clearBlobCache } from '@/routes/IntelligentBoard/DocumentPreviewShape/PdfPreview'
import { clearThumbnailCache, clearDataPreviewCache } from '@/routes/IntelligentBoard/DocumentPreviewShape/DocumentIcon'
import { CONNECTOR_CARD_WIDTH } from '@/routes/IntelligentBoard/ConnectorCardShape'
import type { ConnectorRecord, ConnectorEnumType } from '@/types/connector'
import { isFabricEmbedMode } from '@/lib/fabricEmbedAuth'
import { batchQueryDocs } from '@/utils/batchQueryDocs'

// Re-exported from lib/docType so board-side import sites stay unchanged while
// renderers can reach the same single implementation without pulling the store
// into their module graph (see lib/docType for the why).
import { persistedDocType } from '@/lib/docType'
export { persistedDocType }


const calculateGridPosition = (gridIndex: number) => {
  const col = gridIndex % GRID_LAYOUT.COLUMNS
  const row = Math.floor(gridIndex / GRID_LAYOUT.COLUMNS)
  return {
    x: GRID_LAYOUT.LEFT_OFFSET + col * (ICON_WIDTH + GRID_LAYOUT.MARGIN),
    y: row * (ICON_HEIGHT + GRID_LAYOUT.MARGIN),
  }
}

const calculateConnectorPosition = (connectorIndex: number) => ({
  x: GRID_LAYOUT.LEFT_OFFSET + connectorIndex * (CONNECTOR_CARD_WIDTH + GRID_LAYOUT.MARGIN),
  y: -220,
})

// Board position records carry a synthetic `type` alongside the persisted
// `doc_type` (catalog/connector record type vs. board-level type), so accept
// the optional `type` field here.
type BoardDocumentPosition = DocumentPosition & { type?: string }

// Find the next unoccupied grid slot by checking which positions are already taken
const findNextFreeGridSlot = (selectedDocuments: BoardDocumentPosition[]) => {
  const occupied = new Set<string>()
  for (const doc of selectedDocuments) {
    if (doc.doc_type !== 'connector' && doc.type !== 'connector') {
      occupied.add(`${doc.x},${doc.y}`)
    }
  }
  for (let i = 0; ; i++) {
    const pos = calculateGridPosition(i)
    if (!occupied.has(`${pos.x},${pos.y}`)) {
      return pos
    }
  }
}

// --- saveBoard concurrency coordination (FS-502) ---
// Store-singleton, non-serializable coordination for the save chokepoint. These
// hold a live Promise + flags and only de-duplicate overlapping calls within a
// session, so they live in module scope rather than Zustand state.
//
// Why: every successful saveBoard() replaces the `board` reference, which
// re-runs the BoardCanvas effects (createNodes reposition, store listeners),
// which can re-enter saveBoard(). Without coalescing those re-entries fan out
// into a storm of parallel PUTs (observed at 100+ saves/min to a single board
// on dev) that race and get aborted on navigation/unmount — surfacing to users
// as "Save failed" even though every request the server actually sees is 200.
let saveInFlight: Promise<boolean> | null = null
let rerunRequested = false
let rerunShowToast = false

/**
 * Boards per fetch, matching the data catalog's infinite scroll
 * (`INFINITE_SCROLL_PAGE_SIZE` in DataCatalogV2) so the two lists behave the same.
 *
 * Small on purpose now that loading is on-demand: the first paint costs one small
 * request instead of every board in the tenant — measured unpaged on dev at 114,766
 * boards and 42,964,043 bytes, which killed the tab RENDERING it (FS-696).
 */
export const BOARD_LIST_PAGE_SIZE = 30

export interface BoardSliceState {
  // Boards
  /**
   * UI-84: the board this session just created, while it is still untouched.
   *
   * A board row is created the moment "new board" is clicked — before the user
   * has done anything in it — so opening one and wandering off leaves an empty
   * "Untitled Board N" behind for good. This holds that board's id until the
   * user does something, and `discardProvisionalBoard` drops the row if they
   * never do. Only ever set for a board created in THIS session: an older
   * empty board is somebody's, and not ours to delete.
   */
  provisionalBoardId: string | null
  boardList: Workspace[]
  /** How many boards exist in total — the denominator infinite scroll stops at. */
  boardListTotal: number | null
  /** A page after the first is in flight (drives the list's spinner, not the skeleton). */
  boardListLoadingMore: boolean
  /** Highest page loaded so far, so `loadMoreBoards` knows what to ask for next. */
  boardListPage: number
  boardLocked: boolean
  filteredBoardList: Workspace[]
  showNewBoardModal: boolean
  loadingPreviewImages: boolean
  // Board
  board: Workspace | null
  boardUpdates: UpdateWorkspaceRequest | null
  documents: DocumentRecord[]
  // Optimistic rename overlay: card documentId (the graph-row id for P&ID/PFD
  // cards) -> the locally-applied new name. createNodes consults this FIRST when
  // labelling cards, so a freshly-renamed card isn't clobbered by the rebuild —
  // which otherwise re-derives the label from the source P&ID name (graph cards)
  // or a stale backend-refetched name (dedup-filtered docs). Cleared on board
  // load/reset.
  renamedDocNames: Record<string, string>
  connectors: ConnectorRecord[]
  // TODO: Fix Types
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  selectedDocuments: any
  isSaving: boolean
  showSavingIndicator: boolean
  hasPendingChanges: boolean
  isSavingBlocked: boolean
  showSavedIndicator: boolean
  _savedIndicatorTimeout: NodeJS.Timeout | null
  userHasInteracted: boolean
  isBoardLoading: boolean
  boardLoadError: boolean
  // Fabric embed: restrict DocumentsModal to specific document IDs (null = no restriction)
  allowedDocumentIds: string[] | null
  // Fabric embed: Fabric item IDs for server-side document scoping (null = no restriction)
  fabricItemIds: string[] | null
  // Board-level file search: open/closed. Lives here rather than in BoardCanvas
  // local state because the tldraw context menu is a stable module-scope
  // component (a new reference remounts the canvas), so it can only reach the
  // toggle through the store.
  isBoardFileSearchOpen: boolean
  // Bumped every time the search is asked to open. The panel focuses on each
  // change, so re-triggering the shortcut while the field is ALREADY open puts
  // the caret back in it instead of doing nothing (open is already `true`, so
  // there is no state change to react to).
  boardFileSearchFocusNonce: number
  // Board-level file search (Cmd+F): query used to highlight matching file names on cards
  boardFileSearchQuery: string
  // Index of the currently-focused match (used to auto-navigate the canvas page on
  // search and to step through matches with Up/Down/Enter).
  boardFileSearchMatchIndex: number
  // Board state updates
  loadBoard: (board_id: string) => Promise<void>
  updateBoard: (
    operations?: WorkspaceOperation,
    preview_image?: string | undefined
  ) => void
  // Board list state updates
  loadBoardList: () => Promise<void>
  loadMoreBoards: () => Promise<void>
  filterBoardList: (searchTerm: string) => void
  createBoard: (boardName: string) => Promise<Workspace | undefined>
  /**
   * UI-84: the user did something in this board, so it is theirs to keep.
   * Called by every path that makes a board worth having — a canvas or
   * document change, a rename, a save, a comment, a query. No-op for any board
   * that was not created untouched in this session.
   */
  keepBoard: (boardId: string | null | undefined) => void
  /**
   * UI-84: drop the board if it is still the untouched one this session made.
   * Returns true when a row was deleted.
   */
  discardProvisionalBoard: (boardId: string | null | undefined) => Promise<boolean>
  renameBoard: (boardId: string, newName: string) => Promise<void>
  deleteBoard: (boardId: string) => Promise<boolean>
  duplicateBoard: (boardId: string) => Promise<Workspace | undefined>
  deleteBoardsBulk: (boardIds: string[]) => Promise<{ deletedCount: number; failedIds: string[] }>
  getDocuments: (options?: { docIds?: string[]; skipCacheClear?: boolean }) => Promise<void>
  selectDocument: (
    docId: string,
    isSelected: string | boolean,
    isConnector?: boolean | undefined,
    shouldSave?: boolean | undefined
  ) => void
  setBoardLock: (locked: boolean) => void
  resetBoard: () => void
  saveBoard: (showToast?: boolean) => Promise<boolean>
  saveDocumentSelection: (showToast?: boolean) => Promise<boolean>
  updateSelectedDocuments: (documents: DocumentPosition[]) => void
  removeSelectedDocuments: (documentIds: string[]) => void
  addSelectedDocuments: (documents: DocumentPosition[]) => void
  setIsSaving: (saving: boolean) => void
  setHasPendingChanges: (hasPendingChanges: boolean) => void
  setIsSavingBlocked: (blocked: boolean) => void
  generateDefaultBoardName: () => string
  setUserHasInteracted: (interacted: boolean) => void
  setAllowedDocumentIds: (ids: string[] | null) => void
  setFabricItemIds: (ids: string[] | null) => void
  setBoardFileSearchOpen: (open: boolean) => void
  setBoardFileSearchQuery: (query: string) => void
  setBoardFileSearchMatchIndex: (index: number) => void
  updateDocumentName: (documentId: string, newName: string) => void
  setRenamedDocName: (documentId: string, newName: string) => void
}

export const initialBoardState = {
  provisionalBoardId: null as string | null,
  boardList: [],
  boardListTotal: null,
  boardListLoadingMore: false,
  boardListPage: 0,
  boardLocked: false,
  filteredBoardList: [],
  showNewBoardModal: false,
  loadingPreviewImages: false,
  board: null,
  boardUpdates: null,
  documents: [],
  renamedDocNames: {},
  connectors: [],
  selectedDocuments: [],
  isSaving: false,
  showSavingIndicator: false,
  hasPendingChanges: false,
  isSavingBlocked: false,
  showSavedIndicator: false,
  _savedIndicatorTimeout: null,
  userHasInteracted: false,
  isBoardLoading: false,
  boardLoadError: false,
  allowedDocumentIds: null,
  fabricItemIds: null,
  isBoardFileSearchOpen: false,
  boardFileSearchFocusNonce: 0,
  boardFileSearchQuery: '',
  boardFileSearchMatchIndex: 0,
}

export const boardSlice: StateCreator<
  BoardSliceState & loadingState & authSessionState & ChatState,
  [],
  [],
  BoardSliceState
> = (set, get) => ({
  ...initialBoardState,

  loadBoardList: async () => {
    const { setLoading, filterBoardList } = get()

    try {
      setLoading(true)
      // FIRST PAGE ONLY. The rest arrive from `loadMoreBoards` as the list is
      // scrolled — the same infinite scroll the data catalog uses. Unpaged, this
      // fetched every board in the tenant: 114,766 on dev, 42,964,043 bytes, and a
      // tab that died rendering them, on the route the app lands on after login
      // (FS-696).
      const { items, total } = await apiService.listWorkspaces({
        pageNumber: 0,
        pageSize: BOARD_LIST_PAGE_SIZE,
      })
      set({ boardList: items, boardListTotal: total, boardListPage: 0 })
      filterBoardList('')
    } catch {
      toast.error('Failed to load boards')
    } finally {
      setLoading(false)
    }
  },

  loadMoreBoards: async () => {
    const { boardList, boardListTotal, boardListLoadingMore, boardListPage, filterBoardList } =
      get()
    // The sentinel can fire repeatedly while a page is in flight, and again once the
    // list is complete — both are no-ops rather than duplicate pages.
    if (boardListLoadingMore) return
    if (boardListTotal !== null && boardList.length >= boardListTotal) return

    const nextPage = boardListPage + 1
    set({ boardListLoadingMore: true })
    try {
      const { items, total } = await apiService.listWorkspaces({
        pageNumber: nextPage,
        pageSize: BOARD_LIST_PAGE_SIZE,
      })
      // Append, and de-dupe by id: a board created or reordered between two page
      // fetches can shift across the page boundary and arrive twice.
      const seen = new Set(get().boardList.map((b) => b.id))
      set({
        boardList: [...get().boardList, ...items.filter((b) => !seen.has(b.id))],
        boardListTotal: total ?? get().boardListTotal,
        boardListPage: nextPage,
      })
      filterBoardList('')
    } catch {
      toast.error('Failed to load more boards')
    } finally {
      set({ boardListLoadingMore: false })
    }
  },

  loadBoard: async (board_id) => {
    const { getDocuments } = get()

    // Clear any pending saved indicator timeout from previous board
    const pendingTimeout = get()._savedIndicatorTimeout
    if (pendingTimeout) {
      clearTimeout(pendingTimeout)
    }

    // Immediately set loading state, clear previous board and reset all board-specific state
    // Reset userHasInteracted - will be set to true when user actually interacts with canvas
    // Reset selectedDocuments and boardUpdates to prevent stale data from previous board
    // causing false "unsaved changes" warnings when navigating between boards
    set({
      isBoardLoading: true,
      board: null,
      boardLoadError: false,
      hasPendingChanges: false,
      isSavingBlocked: false,
      showSavingIndicator: false,
      showSavedIndicator: false,
      userHasInteracted: false,
      _savedIndicatorTimeout: null,
      selectedDocuments: [],
      boardUpdates: null,
      // Drop optimistic rename overlays from the previous board.
      renamedDocNames: {},
      // In embed mode, reset to empty array (block all docs) until fresh BOARD_CONTEXT arrives.
      // In standalone mode, stays null (no restriction).
      ...(isFabricEmbedMode() && { allowedDocumentIds: [] as string[] }),
    })

    try {
      const board = await apiService.getWorkspace(board_id)
      const selectedDocuments = board?.documents?.length > 0
        ? board.documents.map((doc: any) => ({ ...doc, doc_type: doc.doc_type }))
        : []
      set({ board, selectedDocuments })
      // Fetch only the board's documents — not the entire catalog.
      // The full catalog is lazy-loaded when DocumentsModal/ConnectorsModal opens.
      const docIds = selectedDocuments
        .filter((d: DocumentPosition) => d.doc_type !== 'connector')
        .map((d: DocumentPosition) => d.document_id)
        .filter(Boolean) as string[]
      await getDocuments({ docIds })
    } catch {
      set({ boardLoadError: true })
      toast.error('Failed to load Board')
    } finally {
      set({ isBoardLoading: false })
    }
  },

  filterBoardList: (searchTerm: string) => {
    const { boardList } = get()

    const filteredData = boardList.filter((board) => {
      const matchesSearch = board.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
      return matchesSearch
    })

    set({ filteredBoardList: filteredData })
  },

  generateDefaultBoardName: () => {
    const { boardList } = get()
    
    // Find all boards with names matching "Untitled Board X"
    const untitledPattern = /^Untitled Board (\d+)$/
    const numbers: number[] = []
    
    boardList.forEach(board => {
      const match = board.name.match(untitledPattern)
      if (match) {
        numbers.push(parseInt(match[1], 10))
      }
    })
    
    // Find the next available number
    let nextNumber = 1
    if (numbers.length > 0) {
      nextNumber = Math.max(...numbers) + 1
    }
    
    return `Untitled Board ${nextNumber}`
  },

  createBoard: async (boardName: string) => {
    const { setLoading } = get()

    set({ showNewBoardModal: false })
    try {
      setLoading(true)
      const board = await apiService.createWorkspace(boardName, [])
      // UI-84: born provisional. The row exists from this moment, but nothing
      // is in it yet — `keepBoard` promotes it the first time the user does
      // anything, and `discardProvisionalBoard` removes it if they never do.
      set({ provisionalBoardId: board?.id ?? null })
      // Refresh board list to include the new board
      await get().loadBoardList()
      return board
    } catch {
      // Annotators are read-only outside the P&ID editor; the API layer already
      // shows the "view/annotate-only" toast, so don't stack a generic error.
      if (!isAnnotatorRole(get().user?.role)) {
        toast.error('Failed to create')
      }
    } finally {
      setLoading(false)
    }
  },

  keepBoard: (boardId) => {
    if (!boardId) return
    if (get().provisionalBoardId !== boardId) return
    set({ provisionalBoardId: null })
  },

  discardProvisionalBoard: async (boardId) => {
    if (!boardId) return false
    const { provisionalBoardId } = get()
    if (provisionalBoardId !== boardId) return false

    // Clear the mark FIRST: a failed delete must not leave the board armed for
    // a second attempt on the next visit, and re-entry while the DELETE is in
    // flight must not fire a second one.
    set({ provisionalBoardId: null })
    try {
      // NOT deleteBoard (greptile): that one catches its own failures and
      // toasts "Failed to delete board". This cleanup was never asked for by
      // the user, so a failure must be invisible — an empty board left behind
      // is the status quo, not a regression worth interrupting them over.
      await apiService.deleteWorkspace(boardId)
      const { context_id, setContextId } = get()
      if (context_id === boardId) setContextId(null)
      await get().loadBoardList()
      return true
    } catch {
      return false
    }
  },

  renameBoard: async (boardId: string, newName: string) => {
    const { board, setLoading, loadBoardList } = get()

    try {
      setLoading(true)
      await apiService.updateWorkspace(boardId, { name: newName })

      // UI-84: promote only once the new name has actually persisted
      // (greptile). Doing it up front meant a failed rename still stopped the
      // board being discarded, leaving exactly the empty board this removes.
      get().keepBoard(boardId)
      
      // Update the current board if it's the one being renamed
      if (board?.id === boardId) {
        set({ board: { ...board, name: newName } })
      }
      
      // Reload the board list from server to ensure consistency
      await loadBoardList()
      
      toast.success('Board renamed successfully')
    } catch {
      toast.error('Failed to rename board')
    } finally {
      setLoading(false)
    }
  },

  getDocuments: async (options?: { docIds?: string[]; skipCacheClear?: boolean }) => {
    const { setLoading, board, allowedDocumentIds } = get()

    if (!board) return

    // Flush stale cache entries from the previous board so the module-level
    // cache stays in sync with the Zustand board lifecycle.
    // Skip when called from DocumentsModal close — the board hasn't changed,
    // only the store's documents array needs updating.
    if (!options?.skipCacheClear) {
      clearDocumentCache()
      clearBlobCache()
      clearThumbnailCache()
      clearDataPreviewCache()
    }

    try {
      setLoading(true)
      // When specific doc IDs are provided (board load path), fetch only those
      // documents instead of the entire catalog. The full catalog is lazy-loaded
      // only when DocumentsModal or ConnectorsModal opens.
      //
      // FS-483: the legacy `/v1/document/query` endpoint hard-codes
      // `page_size: 20` server-side, so a single call past 20 ids silently
      // truncates — any board with >20 docs would end up missing names for
      // later pages and `BoardFileSearch` would report "No matches" for files
      // the user can plainly see. `batchQueryDocs` chunks + parallelises to
      // defeat that cap (see `@/utils/batchQueryDocs`).
      let allDocuments: DocumentRecord[]
      if (options?.docIds !== undefined) {
        // FS-511: always request server-side graph->pnid resolution so
        // BoardCanvas hydrates sources inline (zero follow-up fetches). The
        // rollout flag was retired post-canary; the client batch path in
        // resolvePnidDocuments remains as a fallback if a response lacks
        // inline data.
        allDocuments = await batchQueryDocs(options.docIds, { includePnidSource: true })
      } else {
        allDocuments = await apiService.listDocs()
      }
      const { connectors } = await apiService.getConnectors()

      // In embed mode, filter to only allowed documents before storing.
      // This prevents the full tenant document list from being accessible via DevTools.
      const documents = allowedDocumentIds !== null
        ? allDocuments.filter(doc => allowedDocumentIds.includes(doc.id))
        : allDocuments

      // Pre-populate DocumentPreview cache to prevent N+1 individual GET /document/:id calls.
      // IMPORTANT: Prime AFTER embed-mode filtering to avoid caching restricted documents.
      primeDocumentCache(documents)

      // Start downloading document blobs for the first 2 pages so visible
      // thumbnails render quickly without saturating the network queue.
      // Remaining docs load on-demand via useThumbnail when user paginates.
      // For graph documents (P&ID/PFD), resolve the source PDF document ID.
      const { selectedDocuments } = get()
      const PRELOAD_LIMIT = 2 * DOCS_PER_PAGE
      // Deduplicate and sort to match BoardCanvas's uniqueDocuments order,
      // ensuring we preload the docs that actually appear on pages 1-2.
      const seen = new Set<string>()
      const dedupedDocs = (selectedDocuments as any[]).filter((doc: any) => {
        const id = doc.document_id ?? doc.id
        if (seen.has(id)) return false
        seen.add(id)
        return true
      })
      // No rename overlay: the set() below retires it along with these fresh
      // documents, so BoardCanvas renders this same order.
      const sortedDocs = sortByFilename(dedupedDocs, documents, connectors)
      const blobDocIds: string[] = []
      for (const doc of sortedDocs.slice(0, PRELOAD_LIMIT)) {
        const resolved = resolveBlobDocId(doc, documents)
        if (resolved) blobDocIds.push(resolved)
      }
      if (blobDocIds.length > 0) {
        preloadDocumentBlobs(blobDocIds)
      }

      // Retire optimistic rename overlays: this refetch carries the backend's
      // authoritative names (incl. inline pnid_source for graph cards), so the
      // derived label is now fresh and the overlay is no longer needed. Dropping
      // it here lets a competing update (e.g. a collaborator renaming the same
      // P&ID) win over a now-stale optimistic name, while the overlay still
      // bridges the gap between a rename and the next refresh/reload.
      return set({ documents, connectors, renamedDocNames: {} })
    } catch {
      toast.error('Error fetching documents')
    } finally {
      setLoading(false)
    }
  },

  selectDocument: (docId, isSelected, isConnector = false, shouldSave = false) => {
    const { updateBoard, saveBoard, documents, connectors, allowedDocumentIds } = get()

    // Security: in embed mode, block adding documents not in the allowed list
    if (isSelected && allowedDocumentIds !== null && !allowedDocumentIds.includes(docId)) {
      console.warn('[boardSlice] selectDocument blocked: document not in allowedDocumentIds', docId)
      return
    }

    const selectFrom = isConnector ? connectors : documents
    const docToToggle = selectFrom.find((doc) => doc.id === docId)

    // Resolve doc_type: for graph records, use the source P&ID/PFD type so the
    // board stores the correct visual type rather than 'graph'. `'connector'` is
    // a synthetic board-level type that connector cards persist as `doc_type`.
    let resolvedDocType: DocumentRecord['type'] | ConnectorEnumType | 'connector' =
      docToToggle?.type || 'document'
    if (resolvedDocType === 'graph' && docToToggle && 'properties' in docToToggle && docToToggle.properties?.pnid) {
      const pnidId = docToToggle.properties.pnid
      const sourceDoc = documents.find((d) => d.id === pnidId)
      if (sourceDoc) resolvedDocType = sourceDoc.type
    }
    // `live_stream` documents (the synthetic anchors for streamed / Eventhouse-
    // harvested tags) are TIMESERIES data on a board: persist the workspace
    // link as 'timeseries' — the same doc_type link-time attach writes — so the
    // historian document scope sees post-hoc-attached anchors too (round-2
    // audit: persisting 'live_stream' dead-ended those tags for the agent).
    // Cast: DocumentRecord['type'] doesn't model 'live_stream', but the API
    // returns it for anchor docs (the type union predates them).
    if ((resolvedDocType as string) === 'live_stream') {
      resolvedDocType = 'timeseries'
    }

    // Use functional update to ensure we always read the latest state
    // This prevents race conditions when multiple documents are selected quickly
    let newSelectedDocs: any[] = []
    set((state) => {
      const { selectedDocuments } = state

      if (isSelected) {
        if (!selectedDocuments.some((doc: any) => doc.document_id === docId)) {
          // For newly added documents, find the next free grid slot
          // Connectors go to a separate row; documents fill the grid without gaps
          // `resolvedDocType` can be the synthetic 'connector' literal at runtime
          // (connector cards persist it as doc_type); the static union doesn't
          // carry it after flow-narrowing, so compare as string.
          const isConn = (resolvedDocType as string) === 'connector' || isConnector
          const { x, y } = isConn
            ? calculateConnectorPosition(selectedDocuments.filter((d: any) => d.doc_type === 'connector' || d.type === 'connector').length)
            : findNextFreeGridSlot(selectedDocuments)
          const newDoc = {
            ...docToToggle,
            document_id: docId,
            x,
            y,
            doc_type: resolvedDocType
          }
          newSelectedDocs = [...selectedDocuments, newDoc]
        } else {
          newSelectedDocs = [...selectedDocuments]
        }
      } else {
        newSelectedDocs = selectedDocuments.filter(
          (doc: any) => doc.document_id !== docId
        )
      }

      return { selectedDocuments: newSelectedDocs }
    })

    // Only call updateBoard and saveBoard if explicitly requested
    // This allows modals to batch multiple selections and save once on close
    if (shouldSave) {
      // Read fresh state to ensure we have the latest after set() completed
      const currentSelectedDocs = get().selectedDocuments
      const operations = {
        upsert: currentSelectedDocs.map((doc: any) => ({
          doc_type: persistedDocType(doc),
          document_id: doc.document_id ?? doc.id,
          x: doc.x,
          y: doc.y,
        })),
        remove: isSelected ? [] : [docId],
      }
      updateBoard(operations)
      saveBoard(false)
    }
  },

  // Save the current document selection - builds operations from selectedDocuments and saves
  // Used by modals to batch multiple document selections and save once on close
  // Returns true if save succeeded, false otherwise
  saveDocumentSelection: async (showToast = true): Promise<boolean> => {
    const { selectedDocuments, updateBoard, saveBoard, board } = get()

    if (!board) return false

    // Find documents that were in the board originally but are no longer selected
    const currentDocIds = selectedDocuments.map((doc: any) => doc.document_id ?? doc.id)
    const removedDocIds = board.documents
      .map((doc: any) => doc.document_id)
      .filter((docId: string) => !currentDocIds.includes(docId))

    // Build operations from current selectedDocuments
    const operations = {
      upsert: selectedDocuments.map((doc: any) => ({
        doc_type: persistedDocType(doc),
        document_id: doc.document_id ?? doc.id,
        x: doc.x,
        y: doc.y,
      })),
      remove: removedDocIds,
    }

    // Set userHasInteracted to true since modal selection is an explicit user action
    // This ensures the toast notification is shown
    if (showToast) {
      set({ userHasInteracted: true })
    }

    // Update board state and save
    updateBoard(operations)
    return await saveBoard(showToast)
  },

  setBoardLock: (boardLocked) => {
    set({ boardLocked })
  },

  resetBoard: () => {
    // Clear any pending saved indicator timeout before resetting state
    const pendingTimeout = get()._savedIndicatorTimeout
    if (pendingTimeout) {
      clearTimeout(pendingTimeout)
    }
    // FS-502: cancel any queued trailing save so a board switch can't carry a
    // pending re-save into the next board. We intentionally leave `saveInFlight`
    // alone — nulling a live promise would let a new save start before the
    // current one settles and defeat the concurrency guard. It self-clears in
    // saveBoard's `finally`, and performSave's `savingBoardId` check already
    // keeps any in-flight result applied to the board it was started for.
    rerunRequested = false
    rerunShowToast = false
    // In embed mode, preserve allowedDocumentIds to maintain security restrictions.
    // Resetting to null would remove all document access controls.
    if (isFabricEmbedMode()) {
      const { allowedDocumentIds } = get()
      set({ ...initialBoardState, allowedDocumentIds })
    } else {
      set(initialBoardState)
    }
  },

  updateBoard: (operations, preview_image?: string | undefined) => {
    const { board } = get()

    if (!board) return

    const updates: UpdateWorkspaceRequest = {
      operations: operations,
      // Only include preview_image when explicitly provided - avoids sending
      // large base64 payload with every save (fixes ~4s delay on document saves)
      ...(preview_image && { preview_image }),
    }

    // UI-84: a canvas edit or a document change is the user doing something.
    get().keepBoard(board.id)
    set({ boardUpdates: updates, hasPendingChanges: true })
  },

  saveBoard: async (showToast = true): Promise<boolean> => {
    const { board, boardUpdates, isSavingBlocked } = get()

    // Don't save if blocked (e.g., when leave warning modal is shown)
    if (!board || isSavingBlocked) {
      return false
    }

    // FS-502: no-op guard. With nothing pending there is nothing to PUT, and
    // returning success is the primary brake on the autosave feedback loop:
    // a successful save clears boardUpdates and replaces the `board` reference,
    // which re-runs the canvas effects. Without this guard those effects
    // re-enter saveBoard with an already-flushed payload and PUT an empty
    // update, bumping updated_at and re-triggering themselves at network
    // round-trip cadence (~100+ saves/min observed on dev).
    if (!boardUpdates) {
      return true
    }

    // FS-502: coalesce overlapping saves. boardUpdates always carries full
    // board state (not an incremental delta), so a single trailing re-save
    // after the in-flight one captures the latest edits without dropping any.
    // This caps in-flight saves at one and removes the parallel-PUT storm whose
    // aborted fetches surfaced as "Save failed".
    if (saveInFlight) {
      rerunRequested = true
      rerunShowToast = rerunShowToast || showToast
      return saveInFlight
    }

    // The actual persist. Factored into a local closure so the coalescing
    // driver above can run it once (leading) and at most once more (trailing)
    // without duplicating the body. Each invocation re-reads state via get(),
    // so a trailing save always uses the freshest board/boardUpdates.
    const performSave = async (showToastInner: boolean): Promise<boolean> => {
      const { setLoading, board, boardUpdates, userHasInteracted } = get()
      // Defensive: performSave is only reached with a non-null payload (outer
      // no-op guard for the leading save; drain-loop check for the trailing
      // save). Guard here too so the function is self-contained — a future
      // direct caller can never PUT a null body.
      if (!board || !boardUpdates) return false

      // Capture the board ID we're saving - used to verify we're still on the
      // same board after the await.
      const savingBoardId = board.id

      try {
        setLoading(true)
        // isSaving: always true during save (disables back button); showSavingIndicator:
        // only when we want the "Auto saving..." UI.
        set({ isSaving: true, showSavingIndicator: showToastInner && userHasInteracted })

        const updatedBoard = await apiService.updateWorkspace(
          board.id,
          boardUpdates as UpdateWorkspaceRequest
        )

        // Check if user navigated to a different board while save was in progress.
        // If so, don't update state to avoid corrupting the new board's data.
        const currentBoard = get().board
        if (!currentBoard || currentBoard.id !== savingBoardId) {
          // User navigated away - the save still succeeded on the backend.
          return true
        }

        // Only update the board state with non-visual properties
        // Don't trigger a full board reload that would recreate the canvas
        const newDocuments = updatedBoard.documents || board.documents

        // Sync selectedDocuments with the new positions from the saved board
        // This prevents the createNodes effect from resetting positions to stale values
        // Also filter out any documents that were removed in this save operation
        const { selectedDocuments } = get()
        const removedInThisSave = new Set(
          (boardUpdates as UpdateWorkspaceRequest)?.operations?.remove || []
        )
        const syncedSelectedDocuments = selectedDocuments
          .filter((doc: any) => !removedInThisSave.has(doc.document_id))
          .map((doc: any) => {
            const updatedDoc = newDocuments.find(
              (d: any) => d.document_id === doc.document_id
            )
            if (updatedDoc) {
              return { ...doc, x: updatedDoc.x, y: updatedDoc.y }
            }
            return doc
          })

        // Only update tldraw_snapshot from server if this save included a snapshot.
        // Otherwise keep the current client snapshot to prevent stale server snapshots
        // from overwriting unsaved canvas changes (e.g., drawn lines that were deleted
        // but not yet auto-saved when DocumentsModal triggered a save).
        const savedWithSnapshot = !!(boardUpdates as any)?.operations?.tldraw_snapshot

        // Only clear the pending payload if it's still the one we just sent.
        // If a new change replaced boardUpdates while this PUT was in flight,
        // keep it so the coalescing drain loop runs a trailing save (FS-502) —
        // otherwise that mid-flight edit would wait for the next debounce.
        set((state) => {
          const flushedSentPayload = state.boardUpdates === boardUpdates
          return {
            board: {
              ...board,
              // Update only metadata, not the visual state that would cause recreation
              name: updatedBoard.name,
              updated_at: updatedBoard.updated_at,
              preview_image: updatedBoard.preview_image,
              // Keep the current documents/positions to avoid recreation
              documents: newDocuments,
              tldraw_snapshot: savedWithSnapshot
                ? updatedBoard.tldraw_snapshot
                : board.tldraw_snapshot,
            },
            selectedDocuments: syncedSelectedDocuments,
            boardUpdates: flushedSentPayload ? null : state.boardUpdates,
            hasPendingChanges: flushedSentPayload ? false : state.hasPendingChanges,
          }
        })

        // Show saved indicator for canvas changes, not for document selection or initial load
        if (showToastInner && userHasInteracted) {
          // Clear any existing saved indicator timeout (get fresh value to handle concurrent saves)
          const currentTimeout = get()._savedIndicatorTimeout
          if (currentTimeout) {
            clearTimeout(currentTimeout)
          }

          // Show the saved indicator immediately
          set({ showSavedIndicator: true })

          // Hide the saved indicator after 3 seconds
          const newTimeout = setTimeout(() => {
            set({ _savedIndicatorTimeout: null, showSavedIndicator: false })
          }, 3000)

          set({ _savedIndicatorTimeout: newTimeout })
        } else {
          // Clear any orphaned saved indicator state when save completes without showing indicator
          const currentTimeout = get()._savedIndicatorTimeout
          if (currentTimeout) {
            clearTimeout(currentTimeout)
            set({ _savedIndicatorTimeout: null, showSavedIndicator: false })
          }
        }
        return true
      } catch (error) {
        // Clear any pending saved indicator to prevent showing "saved" after failure
        const pendingTimeout = get()._savedIndicatorTimeout
        if (pendingTimeout) {
          clearTimeout(pendingTimeout)
          set({ _savedIndicatorTimeout: null, showSavedIndicator: false })
        }
        const status = (error as any)?.response?.status
        const message = error instanceof Error ? error.message : String(error)
        console.error(`[BOARD_SAVE_FAILED] board_id=${board.id} status=${status ?? 'unknown'} error=${message}`)
        toast.error('Failed to save board')
        return false
      } finally {
        setLoading(false)
        set({ isSaving: false, showSavingIndicator: false })
      }
    }

    saveInFlight = (async () => {
      let ok = await performSave(showToast)
      // Drain coalesced requests: run exactly one more save while changes
      // remain pending. Terminates once a save has cleared boardUpdates and no
      // newer change has arrived.
      while (rerunRequested) {
        rerunRequested = false
        const nextShowToast = rerunShowToast
        rerunShowToast = false
        if (!get().boardUpdates) break
        ok = await performSave(nextShowToast)
      }
      return ok
    })()

    try {
      return await saveInFlight
    } finally {
      saveInFlight = null
      rerunRequested = false
      rerunShowToast = false
    }
  },

  deleteBoard: async (boardId: string) => {
    const { setLoading, loadBoardList } = get()

    try {
      setLoading(true)
      await apiService.deleteWorkspace(boardId)

      // Clear context_id if the deleted board was currently selected
      const { context_id, setContextId } = get()
      if (context_id === boardId) {
        setContextId(null)
      }

      await loadBoardList()
      return true
    } catch {
      toast.error('Failed to delete board')
      return false
    } finally {
      setLoading(false)
    }
  },

  duplicateBoard: async (boardId: string) => {
    const { setLoading, loadBoardList } = get()
    try {
      setLoading(true)
      const duplicated = await apiService.duplicateWorkspace(boardId)
      await loadBoardList()
      return duplicated
    } catch {
      toast.error('Failed to duplicate board')
      return undefined
    } finally {
      setLoading(false)
    }
  },

  deleteBoardsBulk: async (boardIds: string[]) => {
    const { setLoading, loadBoardList, boardList } = get()

    // Only delete IDs that correspond to a real board in the list.
    const validIds = boardIds.filter(id => {
      const board = boardList.find(b => b.id === id)
      return Boolean(board)
    })

    if (validIds.length === 0) {
      return { deletedCount: 0, failedIds: [] }
    }

    const failedIds: string[] = []
    let deletedCount = 0

    try {
      setLoading(true)

      for (const id of validIds) {
        try {
          await apiService.deleteWorkspace(id)
          deletedCount++

          // Clear context_id if the deleted board was currently selected
          const { context_id, setContextId } = get()
          if (context_id === id) {
            setContextId(null)
          }
        } catch {
          failedIds.push(id)
        }
      }

      // Single reload after all deletions
      await loadBoardList()
    } finally {
      setLoading(false)
    }

    return { deletedCount, failedIds }
  },

  updateSelectedDocuments: (documents: DocumentPosition[]) => {
    const { selectedDocuments } = get()
    const updatedDocs = selectedDocuments.map((doc: any) => {
      const currentDoc = documents.find(d => d.document_id === doc.document_id)
      if (currentDoc) {
        return {
          ...doc,
          x: currentDoc.x,
          y: currentDoc.y,
        }
      }
      return doc
    })
    set({ selectedDocuments: updatedDocs })
  },

  removeSelectedDocuments: (documentIds: string[]) => {
    const { selectedDocuments } = get()
    const updatedDocs = selectedDocuments.filter(
      (doc: any) => !documentIds.includes(doc.document_id)
    )
    set({ selectedDocuments: updatedDocs })
  },

  // Mirror of removeSelectedDocuments for the inverse direction.
  // Used by BoardCanvas when a tldraw shape appears via undo/redo
  // (or any other path that re-adds shapes without going through
  // toggleSelectedDocument). Reconstructs the DocumentPosition from
  // the shape's geometry/meta and appends to selectedDocuments,
  // skipping duplicates so a noisy re-add can't grow the array.
  addSelectedDocuments: (documents: DocumentPosition[]) => {
    const { selectedDocuments } = get()
    const existingIds = new Set(
      selectedDocuments.map((doc: any) => doc.document_id),
    )
    const toAdd = documents.filter(
      (doc) => !existingIds.has(doc.document_id),
    )
    if (toAdd.length === 0) return
    set({ selectedDocuments: [...selectedDocuments, ...toAdd] })
  },

  setIsSaving: (isSaving: boolean) => {
    set({ isSaving })
  },

  setHasPendingChanges: (hasPendingChanges: boolean) => {
    set({ hasPendingChanges })
  },

  setIsSavingBlocked: (isSavingBlocked: boolean) => {
    set({ isSavingBlocked })
  },

  setUserHasInteracted: (userHasInteracted: boolean) => {
    set({ userHasInteracted })
  },

  setAllowedDocumentIds: (allowedDocumentIds: string[] | null) => {
    set({ allowedDocumentIds })
  },
  setFabricItemIds: (fabricItemIds: string[] | null) => {
    set({ fabricItemIds })
  },
  setBoardFileSearchOpen: (isBoardFileSearchOpen: boolean) => {
    if (isBoardFileSearchOpen) {
      set({
        isBoardFileSearchOpen,
        boardFileSearchFocusNonce: get().boardFileSearchFocusNonce + 1,
      })
      return
    }
    // Closing drops the query so the next open starts clean and no stale
    // highlight is left on the cards — same as the panel's own X button.
    set({ isBoardFileSearchOpen, boardFileSearchQuery: '', boardFileSearchMatchIndex: 0 })
  },
  setBoardFileSearchQuery: (boardFileSearchQuery: string) => {
    // Reset focus to the first match whenever the query changes so the canvas
    // jumps to the page of the new top match instead of stale state.
    set({ boardFileSearchQuery, boardFileSearchMatchIndex: 0 })
  },
  setBoardFileSearchMatchIndex: (boardFileSearchMatchIndex: number) => {
    set({ boardFileSearchMatchIndex })
  },

  updateDocumentName: (documentId: string, newName: string) => {
    const { documents } = get()
    set({
      documents: documents.map((doc) =>
        doc.id === documentId ? { ...doc, name: newName } : doc
      ),
    })
  },

  setRenamedDocName: (documentId: string, newName: string) => {
    set((state) => ({
      renamedDocNames: { ...state.renamedDocNames, [documentId]: newName },
    }))
  },
})
